import type { StateCreator } from "zustand";
import type { PersistOptions } from "zustand/middleware";
import { createJSONStorage, persist } from "zustand/middleware";
import { createWithEqualityFn } from "zustand/traditional";

import type { ETermType } from "@schemas/terms";
import type { Term } from "@schemas/terms";

import { useShallow } from "@hooks/useShallow";

import { STORE_KEYS } from "@/config";

type TermsStoreType = {
	terms: Term[] | null;
	setTerms: (userInfo: Term[]) => void;
	clear: () => void;
	getTypeByTermsList: (types: ETermType[]) => Term[] | null;
	getDetailByTermsId: (id: number) => Term | null;
};

type PersistTermsStoreType = (
	config: StateCreator<TermsStoreType>,
	options: PersistOptions<TermsStoreType>,
) => StateCreator<TermsStoreType>;

const store = createWithEqualityFn<TermsStoreType>(
	(persist as PersistTermsStoreType)(
		(set, get) => ({
			terms: null,
			getTypeByTermsList: (types: ETermType[]) => {
				const { terms } = get();
				if (!terms) return null;
				const data: Term[] = [];
				types.forEach((type) => {
					data.push(...terms.filter((term) => term.termType === type));
				});
				return data;
			},
			getDetailByTermsId: (id: number) => {
				const { terms } = get();
				if (!terms) return null;
				return terms.find((term) => term.id === id) ?? null;
			},
			setTerms: (terms) => {
				set({ terms });
			},
			clear: () => set({ terms: null }),
		}),

		{
			name: STORE_KEYS.TERMS,
			storage: createJSONStorage(() => sessionStorage),
			version: 1,
		},
	),
);

export const useTermsStore = <T extends keyof TermsStoreType>(keys: T[]) => {
	return useShallow(store, keys);
};
